import { useState } from 'react';

import { ChannelCard } from '../channels/ChannelCard';
import { useChannelConfigs } from '../channels/use-channels';
import { useCurrentAttempt, useOnboardingAvailability } from '../channels/use-onboarding';
import { WhatsAppConnectCard } from '../channels/WhatsAppConnectCard';
import { WriteCredentialDialog } from '../channels/WriteCredentialDialog';

import type { ChannelConfigWire } from '../channels/types';
import type { ReactElement } from 'react';

export interface ChannelsTabProps {
  mode: 'manage' | 'read-only';
}

/**
 * Aba «Canais» — F2 §4.3. Saiu do «Em breve» em 19/Set/2026.
 *
 * **A pergunta que esta tela responde é «chegou?»**, e ela não tinha resposta em tela: em
 * 15/Set/2026 o `META_APP_SECRET` do hml estava errado, toda entrega da Meta morria na
 * validação de assinatura, e descobrir isso custou dias porque a única fonte era log de
 * container. Cinco superfícies diziam «tudo bem».
 *
 * Por isso a ordem do cartão é diagnóstico → fatos, e por isso o estado não é «tem
 * credencial» (a pergunta que mentiu) mas «credencial **E** último teste ok».
 *
 * `read-only` esconde os botões de mutação; o backend faz defesa em profundidade.
 */
export function ChannelsTab({ mode }: ChannelsTabProps): ReactElement {
  const { data: configs, isLoading, error } = useChannelConfigs();
  const [credencialPara, setCredencialPara] = useState<ChannelConfigWire | null>(null);
  // «Trocar de número» desconecta e pede a escolha do caminho do número novo (revisão de 30/Set,
  // menor 7): o pedido fica aqui porque o cartão que abre a escolha só monta depois.
  const [abrirEscolha, setAbrirEscolha] = useState(false);
  // W2: o botão «Conectar» só existe para quem gerencia; read-only não puxa nem a flag.
  const availability = useOnboardingAvailability(mode === 'manage');
  const attemptQuery = useCurrentAttempt(mode === 'manage');

  if (isLoading) {
    return <div className="p-8 text-sm text-text-secondary">Carregando canais…</div>;
  }
  if (error !== null) {
    return (
      <div className="p-8 text-sm text-red-700" role="alert">
        Falha ao carregar canais: {error.message}
      </div>
    );
  }
  const lista = configs ?? [];
  const whatsapp = lista.find((c) => c.channel === 'whatsapp-cloud-api') ?? null;
  const attempt = attemptQuery.data ?? null;
  const total = lista.length;

  return (
    <div className="flex flex-col p-8">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-text-primary">Canais</h2>
          <p className="mt-0.5 text-xs text-text-secondary">
            Por onde o cliente fala com você.{' '}
            {total === 0
              ? 'Nenhum configurado ainda.'
              : `${String(total)} configurado${total === 1 ? '' : 's'}.`}
          </p>
        </div>
      </div>

      {/* O WhatsApp tem cartão SEMPRE (decisão do TL, 29/Set/2026): é o canal principal, e a
          aba vazia não pode ser um beco sem saída — era o que «Nenhum canal configurado» fazia. */}
      {whatsapp === null && (
        <WhatsAppConnectCard
          canManage={mode === 'manage'}
          attempt={attempt}
          availability={availability.data}
          abrirEscolha={abrirEscolha}
          onEscolhaAberta={() => {
            setAbrirEscolha(false);
          }}
        />
      )}

      {lista.map((c) => (
        <ChannelCard
          key={c.id}
          config={c}
          canManage={mode === 'manage'}
          onTrocarCredencial={setCredencialPara}
          attempt={attempt !== null && attempt.channelConfigId === c.id ? attempt : null}
          onTrocouNumero={() => {
            setAbrirEscolha(true);
          }}
        />
      ))}

      {credencialPara !== null && (
        <WriteCredentialDialog
          config={credencialPara}
          onClose={() => {
            setCredencialPara(null);
          }}
        />
      )}
    </div>
  );
}
